import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import ProductCard from '../components/ProductCard';
import ProductModal from '../components/ProductModal';
import { normalizeCategorias, FALLBACK_CATEGORIAS, categoriaLabel, canonicalCategoriaNombre } from '../utils/categorias';
import { CATALOGO_UNAS_PDF, isManicureCategory } from '../config/unasCatalog';
import { filterCachedProducts, readCatalogCache, writeCatalogCache, clearCatalogCache } from '../utils/catalogCache';

const SORT_OPTIONS = [
  { id: 'catalogo', label: 'Orden del catálogo' },
  { id: 'az', label: 'Alfabéticamente, A-Z' },
  { id: 'za', label: 'Alfabéticamente, Z-A' },
  { id: 'precio-asc', label: 'Precio: menor a mayor' },
  { id: 'precio-desc', label: 'Precio: mayor a menor' },
];

const PAGE_SIZE = 72;

function sortProducts(list, sort) {
  const copy = [...list];
  const name = (p) => String(p.nombre || p.referencia || p.codigo || '').toLowerCase();
  const price = (p) => Number(p.precio) || 0;
  switch (sort) {
    case 'za':
      return copy.sort((a, b) => name(b).localeCompare(name(a), 'es'));
    case 'precio-asc':
      return copy.sort((a, b) => price(a) - price(b));
    case 'precio-desc':
      return copy.sort((a, b) => price(b) - price(a));
    case 'az':
      return copy.sort((a, b) => name(a).localeCompare(name(b), 'es'));
    case 'catalogo':
    default:
      return copy;
  }
}

export default function Shop({ onAdd }) {
  const [params, setParams] = useSearchParams();
  const catRaw = params.get('cat') || 'Todas';
  const cat = catRaw === 'Todas' ? 'Todas' : canonicalCategoriaNombre(catRaw) || catRaw;
  const qParam = params.get('q') || '';
  const sortParam = params.get('sort') || 'catalogo';

  const [q, setQ] = useState(qParam);
  const [debouncedQ, setDebouncedQ] = useState(qParam);
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [preview, setPreview] = useState(null);
  const [sort, setSort] = useState(sortParam);
  const sortRef = useRef(sort);
  sortRef.current = sort;
  const [fromCache, setFromCache] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const scrollLockY = useRef(null);
  const offsetRef = useRef(0);
  const seenRef = useRef(new Set());
  const productosRef = useRef([]);
  const reqIdRef = useRef(0);
  const abortRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadingMoreLock = useRef(false);

  const isUnas = isManicureCategory(cat);

  useEffect(() => {
    productosRef.current = productos;
  }, [productos]);

  function lockScroll() {
    /* kept for API compatibility; categoría ahora sube al inicio */
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    scrollLockY.current = null;
  }

  function restoreScroll() {
    scrollToTop();
  }

  function setCat(c) {
    const next = new URLSearchParams(params);
    if (!c || c === 'Todas') next.delete('cat');
    else next.set('cat', c);
    next.delete('linea');
    setParams(next);
    setFiltersOpen(false);
    scrollToTop();
  }

  const title =
    cat === 'Todas'
      ? 'Todos los productos'
      : categoriaLabel(cat) || cat;

  useEffect(() => {
    setQ(qParam);
    setDebouncedQ(qParam);
  }, [qParam]);

  useEffect(() => {
    setSort(sortParam || 'catalogo');
  }, [sortParam]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  function commitSearch() {
    const next = new URLSearchParams(params);
    if (q.trim()) next.set('q', q.trim());
    else next.delete('q');
    setParams(next);
  }

  useEffect(() => {
    api
      .getCategorias()
      .then((r) => {
        const list = normalizeCategorias(r.data || []);
        setCategorias(list?.length ? list : FALLBACK_CATEGORIAS);
      })
      .catch(() => setCategorias(FALLBACK_CATEGORIAS));
  }, []);

  const fetchPage = useCallback(async ({ reset, preserve = false } = {}) => {
    if (!reset && loadingMoreLock.current) return;

    const keepUi = Boolean(preserve && productosRef.current.length);

    // Cancela solo si es un hard-reset o "cargar más" (preserve no mata la 1ª carga).
    if (!keepUi && abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {
        /* ignore */
      }
    }
    const controller =
      !keepUi && typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) abortRef.current = controller;
    const reqId = ++reqIdRef.current;

    if (reset && !keepUi) {
      offsetRef.current = 0;
      seenRef.current = new Set();
      const cached = readCatalogCache(debouncedQ, cat);
      if (cached?.productos?.length) {
        let list = cached.productos;
        if (cached.fromFallback) list = filterCachedProducts(list, debouncedQ, cat);
        const seen = new Set();
        for (const p of list) seen.add(String(p.id || p.codigo));
        seenRef.current = seen;
        offsetRef.current = list.length;
        productosRef.current = list;
        setProductos(list);
        setTotal(cached.fromFallback ? list.length : cached.total || list.length);
        setFromCache(true);
        setLoading(false);
      } else {
        productosRef.current = [];
        setProductos([]);
        setTotal(0);
        setLoading(true);
      }
      setLoadingMore(false);
      setLoadError('');
      loadingMoreLock.current = false;
    } else if (reset && keepUi) {
      /* refresh en silencio: no vaciar ni mostrar "Cargando…" */
      setLoadError('');
    } else {
      loadingMoreLock.current = true;
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : offsetRef.current;
      const limit = reset && keepUi ? Math.max(PAGE_SIZE, productosRef.current.length) : PAGE_SIZE;
      const res = await api.getProductos({
        q: debouncedQ,
        subcategoria: cat !== 'Todas' ? cat : '',
        offset,
        limit,
        signal: controller?.signal,
      });

      if (reqId !== reqIdRef.current) return;

      const chunk = res.data || [];
      const knownTotal = Number(res.total);
      const seen = reset ? new Set() : seenRef.current;
      let nextAll;
      if (reset) {
        // Primera carga: ordenar una vez. NO reordenar al cargar más (evita que las cards “salten”).
        nextAll = sortProducts(chunk, sortRef.current);
        for (const p of nextAll) seen.add(String(p.id || p.codigo));
      } else {
        nextAll = [...productosRef.current];
        for (const p of chunk) {
          const k = String(p.id || p.codigo);
          if (seen.has(k)) continue;
          seen.add(k);
          nextAll.push(p);
        }
      }
      seenRef.current = seen;
      offsetRef.current = nextAll.length;
      productosRef.current = nextAll;
      setProductos(nextAll);
      if (!chunk.length) {
        setTotal(nextAll.length);
      } else {
        setTotal(Number.isFinite(knownTotal) && knownTotal >= 0 ? knownTotal : nextAll.length);
      }
      setLoadError('');
      setFromCache(false);
      writeCatalogCache(debouncedQ, cat, nextAll, Number.isFinite(knownTotal) ? knownTotal : nextAll.length);
    } catch (err) {
      if (err?.name === 'AbortError' || reqId !== reqIdRef.current) return;
      const keepExisting = productosRef.current.length > 0;
      if (keepExisting) {
        setLoadError('Conexión inestable — se muestran productos ya cargados.');
        setFromCache(true);
      } else {
        const cached = readCatalogCache(debouncedQ, cat);
        if (cached?.productos?.length) {
          let list = cached.productos;
          if (cached.fromFallback) list = filterCachedProducts(list, debouncedQ, cat);
          const seen = new Set();
          for (const p of list) seen.add(String(p.id || p.codigo));
          seenRef.current = seen;
          offsetRef.current = list.length;
          productosRef.current = list;
          setProductos(list);
          setTotal(cached.fromFallback ? list.length : cached.total || list.length);
          setFromCache(true);
          setLoadError('Catálogo en modo offline — mostrando lo último visto.');
        } else {
          setLoadError(err.message || 'No se pudieron cargar los productos.');
          if (reset && !keepUi) {
            setProductos([]);
            setTotal(0);
          }
        }
      }
    } finally {
      if (reqId === reqIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreLock.current = false;
      }
    }
  }, [debouncedQ, cat]);

  // Recarga al cambiar categoría, búsqueda o línea Geles.
  useEffect(() => {
    fetchPage({ reset: true });
  }, [fetchPage]);

  // Invalidación SSE / Admin: refresco suave; eliminación = quitar ya + limpiar caché.
  useEffect(() => {
    let t = null;
    const onInv = (ev) => {
      const detail = ev.detail || {};
      const domains = detail.domains || [];
      if (!domains.includes('product') && !domains.includes('inventory')) return;
      const deleted = detail.reason === 'producto_eliminado' && detail.codigo;
      if (deleted) {
        const codigo = String(detail.codigo);
        setProductos((prev) => {
          const next = prev.filter((p) => String(p.codigo) !== codigo);
          productosRef.current = next;
          return next;
        });
        setTotal((t0) => Math.max(0, t0 - 1));
        seenRef.current.delete(codigo);
        clearCatalogCache();
      }
      clearTimeout(t);
      t = setTimeout(() => {
        fetchPage({ reset: true, preserve: Boolean(deleted && productosRef.current.length) });
      }, deleted ? 120 : 2000);
    };
    window.addEventListener('oh:invalidate', onInv);
    return () => {
      clearTimeout(t);
      window.removeEventListener('oh:invalidate', onInv);
    };
  }, [fetchPage]);

  // Si se queda colgado en "Cargando…", mostrar error con reintento.
  useEffect(() => {
    if (!loading) return undefined;
    const t = setTimeout(() => {
      if (!productosRef.current.length) {
        setLoading(false);
        setLoadError((prev) => prev || 'La carga tardó demasiado. Pulsa Reintentar.');
      }
    }, 28000);
    return () => clearTimeout(t);
  }, [loading]);

  const visible = productos;

  function setSortParam(id) {
    setSort(id);
    const next = new URLSearchParams(params);
    if (!id || id === 'catalogo') next.delete('sort');
    else next.set('sort', id);
    setParams(next);
    // Reordenar solo lo ya cargado, una vez (sin romper el scroll).
    setProductos((prev) => {
      const sorted = sortProducts(prev, id);
      productosRef.current = sorted;
      return sorted;
    });
  }

  const hasMore = productos.length > 0 && productos.length < total && !(loadError && !productos.length);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading || loadingMore) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) fetchPage({ reset: false });
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, fetchPage, productos.length]);

  useLayoutEffect(() => {
    scrollToTop();
  }, [cat, isUnas]);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => scrollToTop());
    return () => window.cancelAnimationFrame(id);
  }, [cat, isUnas]);

  const filterPanel = (
    <aside className="collection-filters">
      <div className="collection-filters-head">
        <h2>Filtros</h2>
        <button type="button" className="collection-filters-close" onClick={() => setFiltersOpen(false)}>
          Cerrar
        </button>
      </div>

      <div className="collection-filter-block">
        <h3>Categoría</h3>
        <ul className="collection-cat-list">
          {categorias.map((c) => {
            const active = cat === 'Todas' ? c.nombre === 'Todas' : cat === c.nombre;
            const label =
              c.label || (c.nombre === 'Todas' ? 'Todas las categorías' : categoriaLabel(c.nombre) || c.nombre);
            return (
              <li key={c.nombre}>
                <button type="button" className={active ? 'active' : ''} onClick={() => setCat(c.nombre)}>
                  <span>{label}</span>
                  {c.total != null ? <em>({c.total})</em> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );

  const showInitialLoading = loading && productos.length === 0;

  return (
    <div className="shop-page collection-page">
      <header className={`collection-hero${isUnas ? ' collection-hero--unas' : ''}${cat !== 'Todas' && !isUnas ? ' collection-hero--cat' : ''}`}>
        <div className="collection-hero-glow" aria-hidden="true" />
        <p className="collection-kicker">Catálogo</p>
        <h1>{title}</h1>
        <p className="collection-hero-meta">
          <span className="collection-count-pill">
            {isUnas
              ? `${visible.length} productos`
              : showInitialLoading
                ? 'Cargando…'
                : `${productos.length}${total ? ` de ${total}` : ''} productos`}
          </span>
          {debouncedQ ? <span className="collection-search-tag">“{debouncedQ}”</span> : null}
        </p>
      </header>

      {isUnas ? (
        <div className="unas-shop-banner">
          <div>
            <strong>Catálogo oficial de Uñas</strong>
            <span>189 páginas del PDF Oh Belleza</span>
          </div>
          <div className="unas-shop-banner-actions">
            <Link to="/catalogo-unas" className="btn-primary">
              Abrir catálogo completo
            </Link>
            <a href={CATALOGO_UNAS_PDF} target="_blank" rel="noreferrer" className="btn-ghost">
              Ver PDF
            </a>
          </div>
        </div>
      ) : null}

      <div className="collection-toolbar">
        <button type="button" className="collection-filters-toggle" onClick={() => setFiltersOpen(true)}>
          Filtros
        </button>
        <div className="collection-search">
          <input
            type="search"
            placeholder="Buscar producto o código..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitSearch();
            }}
          />
          <button type="button" className="shop-search-btn" onClick={commitSearch}>
            Buscar
          </button>
        </div>
        <label className="collection-sort">
          <span>Ordenar por</span>
          <select value={sort} onChange={(e) => setSortParam(e.target.value)}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="collection-layout">
        <div className={`collection-sidebar ${filtersOpen ? 'open' : ''}`}>
          <button
            type="button"
            className="collection-sidebar-backdrop"
            aria-label="Cerrar filtros"
            onClick={() => setFiltersOpen(false)}
          />
          {filterPanel}
        </div>

        <div className="collection-main">
          {loadError ? (
            <p className="error collection-load-error">
              {loadError}{' '}
              <button type="button" className="btn-ghost" onClick={() => fetchPage({ reset: true })}>
                Reintentar
              </button>
            </p>
          ) : null}

          {showInitialLoading ? (
            <p className="loading">Cargando productos...</p>
          ) : (
            <>
              <div className="shop-grid collection-grid">
                {visible.map((p, idx) => (
                  <ProductCard
                    key={`${p.id || ''}-${p.codigo || idx}`}
                    product={p}
                    priority={idx < 8}
                    onPreview={setPreview}
                    onAdd={(prod) => onAdd(prod)}
                  />
                ))}
              </div>
              {hasMore ? <div ref={sentinelRef} className="collection-scroll-sentinel" aria-hidden="true" /> : null}
              {loadingMore ? (
                <p className="loading collection-loading-more">Cargando más productos…</p>
              ) : null}
            </>
          )}

          {!showInitialLoading && !visible.length && !loadError && !hasMore && !loadingMore ? (
            <p className="empty">No hay productos con ese filtro</p>
          ) : null}
        </div>
      </div>

      {preview ? (
        <ProductModal
          product={preview}
          products={visible}
          onSelect={setPreview}
          onClose={() => setPreview(null)}
          onAdd={(prod, opts) => onAdd(prod, opts)}
        />
      ) : null}
    </div>
  );
}
